import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const prefill = params.get("email");
    if (prefill) setEmail(prefill);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(email, password, name);
      toast.success("Tenant provisioned");
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-white/10 aura-grid">
        <Link to="/" className="flex items-center gap-3" data-testid="register-brand">
          <Logo size={44} />
        </Link>
        <div>
          <h2 className="heading-font text-4xl font-black tracking-tighter leading-tight">
            Provision a new<br />cryptographic tenant.
          </h2>
          <p className="mono-font text-xs text-white/50 mt-4 max-w-md leading-relaxed">
            Multi-tenant, unlimited events, hard ticket caps, autonomous agents, SHA-256 identity
            seeds, append-only ledger. Sub-30 seconds to first event.
          </p>
        </div>
        <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
          © 2026 Zayne Crouse · Patent DTTM-001
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-16">
        <div className="w-full max-w-md">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Session · Register</div>
          <h1 className="heading-font text-3xl font-black tracking-tighter">Initialize vault.</h1>
          <p className="text-white/50 text-sm mt-2">Create your tenant to begin.</p>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="google-register-btn"
            className="mt-8 w-full flex items-center justify-center gap-3 px-5 py-3 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors duration-200"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">or</div>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Name</label>
              <input
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm text-white focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm text-white focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                placeholder="you@aura.io"
              />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Password</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm text-white focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                placeholder="Minimum 6 characters"
              />
            </div>
            {error && (
              <div data-testid="register-error" className="mono-font text-xs text-[#FF0033] border border-[#FF0033]/40 px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="register-submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60"
            >
              {submitting ? "Provisioning…" : "Initialize Vault"}
              <ArrowRight weight="bold" size={14} />
            </button>
          </form>

          <div className="mt-8 mono-font text-xs text-white/50">
            Already a tenant?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-[#00FF66] hover:text-white transition-colors duration-200">
              Log in →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
